import React from 'react';
import PropTypes from 'prop-types';
import {AuthContext} from '../../lib/auth/auth-context.jsx';
import AuthModal from '../auth/auth-modal.jsx';
import AuthButton from '../auth/auth-button.jsx';
import NewProjectPopup from '../new-project-popup/new-project-popup.jsx';
import {APP_NAME} from '../../lib/brand.js';
import {apiUrl} from '../../lib/api-base.js';
import {getAppUrl, navigateTo} from '../../lib/nav.js';
import styles from './homepage.css';

const ProjectCard = ({project, onClick}) => (
    <div
        className={styles.projectCard}
        onClick={() => onClick(project)}
    >
        <img
            className={styles.projectThumb}
            src={project.thumbnail}
            alt={project.title}
            loading="lazy"
            onError={e => {
                e.target.style.background = 'linear-gradient(135deg, rgba(212,116,140,0.2), rgba(180,140,200,0.2))';
            }}
        />
        <div className={styles.projectInfo}>
            <p className={styles.projectTitle}>{project.title}</p>
            <p className={styles.projectAuthor}>by {project.author}</p>
            {(project.love_count > 0 || project.view_count > 0) && (
                <div className={styles.projectStats}>
                    {project.love_count > 0 && <span>{'\u2764'} {project.love_count}</span>}
                    {project.view_count > 0 && <span>{'\uD83D\uDC41'} {project.view_count}</span>}
                </div>
            )}
        </div>
    </div>
);
ProjectCard.propTypes = {
    project: PropTypes.object.isRequired,
    onClick: PropTypes.func.isRequired
};

const SkeletonRow = () => (
    <div className={styles.loadingRow}>
        {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className={styles.skeletonCard} />
        ))}
    </div>
);

class Homepage extends React.Component {
    static contextType = AuthContext;
    constructor (props) {
        super(props);
        this.state = {
            featured: [],
            trending: [],
            recent: [],
            recentlyPlayed: [],
            isLoading: true,
            recentLoading: false,
            showAuthModal: false,
            showNewProjectPopup: false
        };
        this.handleProjectClick = this.handleProjectClick.bind(this);
        this.openAuthModal = this.openAuthModal.bind(this);
        this.closeAuthModal = this.closeAuthModal.bind(this);
        this.openNewProjectPopup = this.openNewProjectPopup.bind(this);
        this.closeNewProjectPopup = this.closeNewProjectPopup.bind(this);
    }
    openAuthModal () {
        this.setState({showAuthModal: true});
    }
    closeAuthModal () {
        this.setState({showAuthModal: false});
    }
    openNewProjectPopup () {
        this.setState({showNewProjectPopup: true});
    }
    closeNewProjectPopup () {
        this.setState({showNewProjectPopup: false});
    }
    componentDidMount () {
        this.fetchFeatured();
        this.fetchRecentlyPlayed();
    }
    componentDidUpdate (prevProps, prevState, prevContext) {
        const wasAuth = this._prevAuth;
        const isAuth = this.context && this.context.user;
        this._prevAuth = isAuth;
        if (!wasAuth && isAuth) {
            this.fetchRecentlyPlayed();
        }
    }
    fetchFeatured () {
        const parseData = data => {
            const getThumb = id => `https://uploads.scratch.mit.edu/get_image/project/${id}_280x210.png`;
            const mapList = list => (list || []).map(p => ({
                id: p.id,
                title: p.title,
                author: p.creator || (p.author && p.author.username) || 'Scratcher',
                thumbnail: p.thumbnail || getThumb(p.id),
                love_count: p.love_count || 0,
                view_count: p.view_count || 0
            }));
            return {
                featured: mapList(data.featured || data.community_featured_projects),
                trending: mapList(data.trending || data.community_most_loved_projects),
                recent: mapList(data.recent || data.community_most_remixed_projects)
            };
        };

        fetch(apiUrl('/api/scratch/featured'))
            .then(res => {
                const ct = res.headers.get('content-type') || '';
                if (res.ok && ct.includes('application/json')) return res.json();
                throw new Error('API not available, using public fallback');
            })
            .then(data => {
                const parsed = parseData(data);
                this.setState({
                    featured: parsed.featured,
                    trending: parsed.trending,
                    recent: parsed.recent,
                    isLoading: false
                });
            })
            .catch(() => {
                fetch('https://trampoline.turbowarp.org/proxy/featured')
                    .then(res => res.json())
                    .then(data => {
                        const parsed = parseData(data);
                        this.setState({
                            featured: parsed.featured,
                            trending: parsed.trending,
                            recent: parsed.recent,
                            isLoading: false
                        });
                    })
                    .catch(() => {
                        this.setState({isLoading: false});
                    });
            });
    }
    fetchRecentlyPlayed () {
        const ctx = this.context;
        if (!ctx || !ctx.user) return;
        this.setState({recentLoading: true});
        fetch(apiUrl('/api/activity/recent'), {credentials: 'include'})
            .then(res => {
                const ct = res.headers.get('content-type') || '';
                return res.ok && ct.includes('application/json') ? res.json() : {};
            })
            .then(data => {
                this.setState({
                    recentlyPlayed: data.projects || [],
                    recentLoading: false
                });
            })
            .catch(() => {
                this.setState({recentLoading: false});
            });
    }
    handleProjectClick (project) {
        const ctx = this.context;
        if (ctx && ctx.user) {
            fetch(apiUrl('/api/activity/view'), {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                credentials: 'include',
                body: JSON.stringify({
                    projectId: project.id,
                    title: project.title,
                    author: project.author
                })
            }).catch(() => {});
        }
        navigateTo(`/editor.html#${project.id}`);
    }
    renderSection (title, projects, linkHref, linkText) {
        return (
            <div className={styles.section}>
                <div className={styles.sectionHeader}>
                    <h2 className={styles.sectionTitle}>{title}</h2>
                    {linkHref && (
                        <a href={getAppUrl(linkHref)} className={styles.sectionLink}>
                            {linkText || 'See all'}
                        </a>
                    )}
                </div>
                {projects.length > 0 ? (
                    <div className={styles.projectsRow}>
                        {projects.map(p => (
                            <ProjectCard
                                key={p.id}
                                project={p}
                                onClick={this.handleProjectClick}
                            />
                        ))}
                    </div>
                ) : (
                    <div className={styles.emptyMessage}>No projects found</div>
                )}
            </div>
        );
    }
    render () {
        const ctx = this.context;
        const user = ctx ? ctx.user : null;
        const {featured, trending, recent, recentlyPlayed, isLoading, recentLoading} = this.state;

        return (
            <div className={styles.pageWrapper}>
                <nav className={styles.navBar}>
                    <a href={getAppUrl('/')} className={styles.navLogo}>{APP_NAME}</a>
                    <div className={styles.navLinks}>
                        <a href={getAppUrl('/')} className={`${styles.navLink} ${styles.navLinkActive}`}>Home</a>
                        <a href={getAppUrl('/explore')} className={styles.navLink}>Explore</a>
                        <button
                            className={`${styles.navLink} ${styles.navLinkButton}`}
                            onClick={this.openNewProjectPopup}
                        >Create</button>
                        {user && (
                            <a href={getAppUrl('/my-projects')} className={styles.navLink}>My Projects</a>
                        )}
                    </div>
                    <div className={styles.navUser}>
                        <AuthButton />
                    </div>
                </nav>
                {this.state.showAuthModal && (
                    <AuthModal
                        onClose={this.closeAuthModal}
                        onSuccess={() => {
                            this.closeAuthModal();
                            if (this.context && this.context.refresh) {
                                this.context.refresh();
                            }
                        }}
                    />
                )}
                {this.state.showNewProjectPopup && (
                    <NewProjectPopup
                        onClose={this.closeNewProjectPopup}
                    />
                )}

                <div className={styles.hero}>
                    <h1 className={styles.heroTitle}>Create, Explore, and Share</h1>
                    <p className={styles.heroSubtitle}>
                        {APP_NAME} lets you run Scratch projects faster with powerful extensions and tools
                    </p>
                    <div className={styles.heroButtons}>
                        <button
                            className={styles.heroBtnPrimary}
                            onClick={this.openNewProjectPopup}
                        >Start Creating</button>
                        <a href={getAppUrl('/explore')} className={styles.heroBtnSecondary}>Explore Projects</a>
                    </div>
                </div>

                {user && recentlyPlayed.length > 0 && (
                    this.renderSection('Recently Played', recentlyPlayed, null, null)
                )}

                {isLoading ? (
                    <div className={styles.section}>
                        <h2 className={styles.sectionTitle}>Featured Projects</h2>
                        <SkeletonRow />
                    </div>
                ) : (
                    <React.Fragment>
                        {this.renderSection('Featured Projects', featured, '/explore', 'Explore more')}
                        {this.renderSection('Most Loved', trending, '/explore?mode=popular', 'See all')}
                        {this.renderSection('Most Remixed', recent, '/explore?mode=recent', 'See all')}
                    </React.Fragment>
                )}

                <div className={styles.footer}>
                    <p>{APP_NAME} is based on Scratch by MIT Media Lab and TurboWarp.</p>
                </div>
            </div>
        );
    }
}

export default Homepage;
